import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Label } from "@groot/ui/label";
import { Switch } from "@/app/shared/ui/switch";
import { cn, netWorthContribution } from "@/lib/utils";
import type { Account } from "@/types";
import { Building2, ChevronDown, ChevronRight, CreditCard, Landmark, Wallet } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface AllAccountsCardProps {
  accounts: Account[];
  isLoading?: boolean;
}

export function AllAccountsCard({ accounts, isLoading }: AllAccountsCardProps) {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Filter accounts based on archived toggle
  const filteredAccounts = showArchived
    ? accounts.filter((account) => !account.isDeleted)
    : accounts.filter((account) => !account.isArchived && !account.isDeleted);

  const handleAccountClick = (accountId: number) => {
    navigate(`/accounts/${accountId}`);
  };

  const handleAllAccountsClick = () => {
    navigate("/transactions");
  };

  // Group accounts by type
  const bankAccounts = filteredAccounts.filter((acc) => acc.type === "bank");
  const creditCards = filteredAccounts.filter((acc) => acc.type === "credit_card");
  const loans = filteredAccounts.filter((acc) => acc.type === "loan");
  const wallets = filteredAccounts.filter((acc) => acc.type === "digital_wallet");
  const crypto = filteredAccounts.filter((acc) => acc.type === "cryptocurrency");

  if (isLoading) {
    return (
      <div className="border border-border rounded-lg overflow-hidden bg-background">
        <div className="border-b border-border bg-muted px-6 py-4">
          <h2 className="text-base font-medium text-foreground">All Accounts</h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {[...Array(8)].map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: using index for skeleton loader is fine
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const renderAccountGroup = (title: string, accountList: Account[], icon: React.ReactNode) => {
    if (accountList.length === 0) return null;

    const isExpanded = expandedCategories.has(title);

    // Calculate category total
    // For debt accounts (credit cards, loans), we show the amount owed as positive
    const categoryTotal = accountList.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);

    const toggleCategory = () => {
      setExpandedCategories((prev) => {
        const next = new Set(prev);
        if (next.has(title)) {
          next.delete(title);
        } else {
          next.add(title);
        }
        return next;
      });
    };

    return (
      <div className="mb-4 last:mb-0">
        <button
          type="button"
          onClick={toggleCategory}
          className="flex items-center gap-1.5 mb-2 px-2 w-full hover:bg-muted rounded py-1"
        >
          {isExpanded ? (
            <ChevronDown className="size-3 text-muted-foreground/70" />
          ) : (
            <ChevronRight className="size-3 text-muted-foreground/70" />
          )}
          {icon}
          <h3 className="text-xs font-medium text-muted-foreground uppercase">{title}</h3>
          <span className="text-xs text-muted-foreground/70 ml-auto font-normal">
            <AmountDisplay amount={categoryTotal} />
          </span>
        </button>
        {isExpanded && (
          <div className="space-y-1 ml-3">
            {accountList.map((account) => (
              <button
                type="button"
                key={account.id}
                onClick={() => handleAccountClick(account.id)}
                className="w-full text-left px-3 py-2 rounded-md hover:bg-muted"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {account.iconUrl && (
                      <img
                        src={account.iconUrl}
                        alt=""
                        className="size-4 rounded flex-shrink-0"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                    <span className="text-sm truncate">{account.accountName}</span>
                  </div>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    <AmountDisplay
                      amount={account.currentBalance || 0}
                      currencyCode={account.currencyCode || "INR"}
                    />
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Net worth: assets minus what is owed on cards and loans
  const netWorth = filteredAccounts.reduce((sum, acc) => sum + netWorthContribution(acc), 0);

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-background">
      <div className="border-b border-border bg-muted px-6 py-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-foreground">All Accounts</h2>
          <div className="flex items-center space-x-2">
            <Switch
              id="show-archived-accounts"
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <Label
              htmlFor="show-archived-accounts"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              View Archived
            </Label>
          </div>
        </div>
      </div>
      <div className="p-6">
        {/* All Accounts Button */}
        <button
          type="button"
          onClick={handleAllAccountsClick}
          className={cn(
            "w-full text-left px-3 py-2 rounded-md mb-4",
            "hover:bg-muted bg-muted/50 font-medium",
          )}
        >
          <div className="flex items-center gap-2">
            <Wallet className="size-4" />
            <span className="text-sm">All Accounts</span>
          </div>
        </button>

        {/* Account Groups */}
        {renderAccountGroup("Bank Accounts", bankAccounts, <Building2 className="size-3.5" />)}
        {renderAccountGroup("Credit Cards", creditCards, <CreditCard className="size-3.5" />)}
        {renderAccountGroup("Loans", loans, <Landmark className="size-3.5" />)}
        {renderAccountGroup("Digital Wallets", wallets, <Wallet className="size-3.5" />)}
        {renderAccountGroup("Cryptocurrency", crypto, <Wallet className="size-3.5" />)}

        {/* Net Worth */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex justify-between items-center px-3">
            <span className="text-sm font-medium text-muted-foreground">Net Worth</span>
            <span className="text-base font-medium text-foreground">
              <AmountDisplay amount={netWorth} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
